import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useGeminiLiveSession } from '../hooks/useGeminiLiveSession'
import Icon from '../components/icons/Icon'
import '../styles/voice.css'

/**
 * Live voice session page.
 *
 * The session log the hook produces is engineering telemetry — socket state,
 * sample rates, turn boundaries — interleaved with the actual conversation.
 * Rendering it verbatim, as this page used to, showed the user nine lines of
 * diagnostics for every two lines of speech. Here the two are separated: the
 * conversation reads as a transcript, and the diagnostics stay available but
 * collapsed, which is where they're useful without being the whole page.
 */

type Turn = { speaker: 'user' | 'assistant'; text: string; at: string }

/**
 * Longest pause treated as "still the same spoken turn".
 *
 * Transcription fragments land a few hundred ms apart; a typed message is a
 * deliberate act seconds later. Two seconds separates them comfortably without
 * splitting a sentence at a natural pause.
 */
const FRAGMENT_GAP_MS = 2000

/** Human label for the session state machine. */
const STATUS_LABEL: Record<string, string> = {
  active: 'Live',
  connected: 'Connected',
  connecting: 'Connecting',
  failed: 'Connection failed',
  idle: 'Not started',
}

const UserQuestionsGeminiPage: React.FC = () => {
  const {
    isConnected,
    isSessionActive,
    status,
    error,
    logs,
    inputLevelRef,
    outputLevelRef,
    startSession,
    stopSession,
    sendTextMessage,
  } = useGeminiLiveSession()

  const [inputText, setInputText] = useState('')
  const [busy, setBusy] = useState(false)
  const transcriptEndRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)

  // Split the single log stream into the two things it actually contains.
  //
  // Speech arrives as a burst of transcription fragments a few hundred ms
  // apart, which have to be stitched back into one paragraph. Typed messages
  // arrive as whole thoughts, seconds apart, and are separate turns. The gap
  // between entries is what distinguishes them.
  //
  // Fragments carry their own leading whitespace, so they're concatenated
  // directly and the result is normalised -- inserting a separator here would
  // double the spaces mid-sentence.
  const { turns, diagnostics } = useMemo(() => {
    const turnsOut: Turn[] = []
    const diagOut: typeof logs = []
    let lastAt = 0

    for (const entry of logs) {
      if (entry.tag === 'user' || entry.tag === 'assistant') {
        const at = new Date(entry.timestamp).getTime()
        const last = turnsOut[turnsOut.length - 1]
        const isFragmentOfSameTurn =
          last && last.speaker === entry.tag && at - lastAt <= FRAGMENT_GAP_MS

        if (isFragmentOfSameTurn) {
          last.text = `${last.text}${entry.message}`.replace(/\s+/g, ' ').trim()
        } else {
          turnsOut.push({
            speaker: entry.tag,
            text: entry.message.replace(/\s+/g, ' ').trim(),
            at: entry.timestamp,
          })
        }
        lastAt = at
      } else {
        diagOut.push(entry)
      }
    }

    return { turns: turnsOut, diagnostics: diagOut }
  }, [logs])

  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [turns.length])

  // Drive the level meter straight from the audio refs into CSS custom
  // properties. Going through state would re-render the page on every audio
  // chunk; this touches two style values per frame and never re-renders.
  useEffect(() => {
    if (!isSessionActive) {
      stageRef.current?.style.setProperty('--in', '0')
      stageRef.current?.style.setProperty('--out', '0')
      return
    }

    let frame = 0
    const tick = () => {
      const el = stageRef.current
      if (el) {
        el.style.setProperty('--in', inputLevelRef.current.toFixed(3))
        el.style.setProperty('--out', outputLevelRef.current.toFixed(3))
      }
      frame = requestAnimationFrame(tick)
    }

    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [isSessionActive, inputLevelRef, outputLevelRef])

  const handleToggle = async () => {
    setBusy(true)
    try {
      if (isConnected) {
        await stopSession()
      } else {
        await startSession()
      }
    } finally {
      setBusy(false)
    }
  }

  const handleSendText = (e: React.FormEvent) => {
    e.preventDefault()
    const text = inputText.trim()
    if (!text) return
    sendTextMessage(text)
    setInputText('')
  }

  const connecting = status === 'connecting' || busy
  const statusLabel = connecting ? 'Connecting' : STATUS_LABEL[status] ?? 'Not started'

  return (
    <div className="voice">
      <header className="voice-head">
        <div>
          <h1 className="voice-title">Talk it through</h1>
          <p className="voice-sub">
            A live voice conversation about your money — ask follow-ups, think
            out loud, or work through what you just shared.
          </p>
        </div>
        <span className={`voice-status voice-status--${status}`} role="status">
          <span className="voice-status-dot" aria-hidden="true" />
          {statusLabel}
        </span>
      </header>

      {error && (
        <div className="voice-error" role="alert">
          <Icon name="alert" className="voice-error-icon" />
          <span>{error}</span>
        </div>
      )}

      <div
        ref={stageRef}
        className={`voice-stage${isSessionActive ? ' is-live' : ''}`}
      >
        <button
          type="button"
          className="voice-orb"
          onClick={handleToggle}
          disabled={connecting}
          aria-pressed={isConnected}
          aria-label={isConnected ? 'End session' : 'Start session'}
        >
          {/* Two rings: the inner one tracks your microphone, the outer one
              tracks the reply audio, so it's always clear who is speaking. */}
          <span className="voice-ring voice-ring--out" aria-hidden="true" />
          <span className="voice-ring voice-ring--in" aria-hidden="true" />
          <span className="voice-orb-face">
            <Icon name={isConnected ? 'close' : 'mic'} size={22} />
          </span>
        </button>

        <p className="voice-hint">
          {connecting
            ? 'Opening the audio session…'
            : isSessionActive
              ? 'Listening — just start talking'
              : isConnected
                ? 'Connected, waiting for audio'
                : 'Start a session to begin'}
        </p>
      </div>

      <div className="voice-transcript" aria-live="polite">
        {turns.length === 0 ? (
          <p className="voice-empty">
            {isSessionActive
              ? 'Your conversation will appear here as you speak.'
              : 'No conversation yet.'}
          </p>
        ) : (
          turns.map((turn, i) => (
            <div key={`${turn.at}-${i}`} className={`voice-turn voice-turn--${turn.speaker}`}>
              <span className="voice-speaker">{turn.speaker === 'user' ? 'You' : 'Vault'}</span>
              <p className="voice-text">{turn.text}</p>
            </div>
          ))
        )}
        <div ref={transcriptEndRef} />
      </div>

      <form className="voice-composer" onSubmit={handleSendText}>
        <input
          id="textMessageInput"
          type="text"
          placeholder={isSessionActive ? 'Or type instead…' : 'Start a session to type'}
          autoComplete="off"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          disabled={!isSessionActive}
        />
        <button type="submit" disabled={!isSessionActive || !inputText.trim()}>
          Send
        </button>
      </form>

      {diagnostics.length > 0 && (
        <details className="voice-diag">
          <summary>
            Session details
            <span className="voice-diag-count">{diagnostics.length}</span>
          </summary>
          <div className="voice-diag-body">
            {diagnostics.map((entry, i) => (
              <div
                key={`${entry.timestamp}-${i}`}
                className={`voice-diag-row${entry.tag === 'error' ? ' is-error' : ''}`}
              >
                <time>{new Date(entry.timestamp).toLocaleTimeString()}</time>
                <span>{entry.message}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  )
}

export default UserQuestionsGeminiPage
